import React, { useState } from 'react';
import { FiPayLogo } from './FiPayLogo';
import { Menu, X, UserCheck, LogIn, UserPlus, Star, LayoutDashboard } from 'lucide-react';

const NAV_LINKS = [
  { label: 'All Solutions', hash: 'solutions-section' },
  { label: 'Refer & Earn', hash: 'refer-earn-section', accent: true },
  { label: 'Reviews', hash: 'reviews-section', star: true },
  { label: 'About Us', hash: 'about-section' },
];

export const Navbar = ({ onNavigate, onOpenInquiry, isLoggedIn }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const go = (view, hash) => {
    setMobileMenuOpen(false);
    onNavigate(view, hash);
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-white/95 backdrop-blur border-b border-slate-200 shadow-2xs">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18">
          <button
            onClick={() => go('home')}
            className="flex items-center text-left focus:outline-none cursor-pointer"
            aria-label="Fi Pay Bills home"
          >
            <FiPayLogo size="md" />
          </button>

          {/* Desktop links */}
          <nav className="hidden lg:flex items-center gap-7 text-sm font-semibold text-slate-700">
            {NAV_LINKS.map((link) => (
              <button
                key={link.hash}
                onClick={() => go('home', link.hash)}
                className={`transition-colors cursor-pointer flex items-center gap-1 ${
                  link.accent ? 'text-amber-900 font-bold hover:text-amber-700' : 'hover:text-blue-700'
                }`}
              >
                {link.star && <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />}
                <span>{link.label}</span>
              </button>
            ))}
          </nav>

          {/* Desktop actions */}
          <div className="hidden lg:flex items-center gap-2.5">
            <button
              onClick={() => onOpenInquiry('Become a Partner (Refer & Earn)')}
              className="px-3.5 py-2 rounded-xl text-xs font-bold text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-300 transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <UserCheck className="w-3.5 h-3.5 text-amber-700" />
              <span>Become a Partner</span>
            </button>

            {isLoggedIn ? (
              <button
                onClick={() => go('dashboard')}
                className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-blue-700 hover:bg-blue-800 transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm"
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                <span>Go to Dashboard</span>
              </button>
            ) : (
              <>
                <button
                  onClick={() => go('signin')}
                  className="px-3.5 py-2 text-xs font-bold text-blue-900 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-xl transition-colors cursor-pointer flex items-center gap-1.5"
                >
                  <LogIn className="w-3.5 h-3.5 text-blue-700" />
                  <span>Sign In</span>
                </button>
                <button
                  onClick={() => go('signup')}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-blue-700 hover:bg-blue-800 transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  <span>Register</span>
                </button>
              </>
            )}
          </div>

          {/* Mobile actions */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={() => go(isLoggedIn ? 'dashboard' : 'signin')}
              className="px-2.5 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold cursor-pointer"
            >
              {isLoggedIn ? 'Dashboard' : 'Sign In'}
            </button>
            <button
              onClick={() => onOpenInquiry()}
              className="px-3 py-1.5 bg-blue-700 text-white rounded-lg text-xs font-bold cursor-pointer"
            >
              Enquire
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-700 hover:bg-slate-100 rounded-lg cursor-pointer"
              aria-label="Toggle navigation"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-3">
          <div className="flex flex-col space-y-1 text-sm font-semibold text-slate-700">
            {NAV_LINKS.map((link) => (
              <button
                key={link.hash}
                onClick={() => go('home', link.hash)}
                className={`text-left py-2 cursor-pointer flex items-center gap-1.5 ${
                  link.accent ? 'text-amber-900 font-bold' : 'hover:text-blue-700'
                }`}
              >
                {link.star && <Star className="w-4 h-4 fill-amber-400 text-amber-400" />}
                <span>{link.label}</span>
              </button>
            ))}
          </div>

          <div className="pt-3 border-t border-slate-100 flex flex-col gap-2">
            {isLoggedIn ? (
              <button
                onClick={() => go('dashboard')}
                className="w-full py-2.5 rounded-xl border border-blue-200 text-blue-800 bg-blue-50 font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <LayoutDashboard className="w-3.5 h-3.5 text-blue-700" />
                <span>Go to Dashboard</span>
              </button>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => go('signin')}
                  className="py-2.5 rounded-xl border border-blue-200 text-blue-800 bg-blue-50 font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <LogIn className="w-3.5 h-3.5 text-blue-700" />
                  <span>Sign In</span>
                </button>
                <button
                  onClick={() => go('signup')}
                  className="py-2.5 rounded-xl border border-slate-200 text-slate-800 bg-white font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  <span>Register</span>
                </button>
              </div>
            )}
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                onOpenInquiry();
              }}
              className="w-full py-2.5 rounded-xl bg-blue-700 text-white font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer"
            >
              Submit an Enquiry
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
